/**
 * useMasterList.js
 * ----------------
 * Industry-standard reusable headless React hook for master list screens.
 * 
 * Stability & Performance Architecture:
 * 1. Uses useRef for method references (apiClient, fetchList, archiveItem, deleteItem)
 *    so changing references or re-renders NEVER cause effect invalidation or loops.
 * 2. Serializes extraParams via useMemo(JSON.stringify) to avoid reference inequality.
 * 3. useEffect triggers EXCLUSIVELY on primitive values: [page, pageSize, viewMode, searchText, serializedExtra].
 * 4. Zero infinite loops, unified AppToast feedback, and AppConfirmModal integration.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { appToast } from '../../../components/AppToast';
import { appConfirm } from '../../../components/AppConfirmModal';

const DEFAULT_PAGE_SIZE = 10;

export function useMasterList({
  entityName = 'Record',
  apiClient = null,
  fetchList = null,
  archiveItem = null,
  deleteItem = null,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  extraParams = {},
}) {
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived' | 'all'
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: defaultPageSize,
    totalPages: 1,
  });

  // Method references stored in refs to prevent dependency invalidation
  const fetchListRef = useRef(fetchList);
  fetchListRef.current = fetchList;
  const archiveItemRef = useRef(archiveItem);
  archiveItemRef.current = archiveItem;
  const deleteItemRef = useRef(deleteItem);
  deleteItemRef.current = deleteItem;
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  // Resolve method helper
  const getListFn = useCallback(() => {
    if (typeof fetchListRef.current === 'function') return fetchListRef.current;
    const client = apiClientRef.current;
    if (!client) return null;
    const targets = [client, client.default, client.client].filter(Boolean);
    const candidateNames = ['list', `list${entityName}s`, `list${entityName}`, 'getAll'];
    for (const target of targets) {
      for (const name of candidateNames) {
        if (typeof target[name] === 'function') {
          return target[name].bind(target);
        }
      }
    }
    return null;
  }, [entityName]);

  const getArchiveFn = useCallback(() => {
    if (typeof archiveItemRef.current === 'function') return archiveItemRef.current;
    const client = apiClientRef.current;
    if (!client) return null;
    const targets = [client, client.default, client.client].filter(Boolean);
    const candidateNames = ['archive', `archive${entityName}`, 'archiveItem', 'toggleArchive'];
    for (const target of targets) {
      for (const name of candidateNames) {
        if (typeof target[name] === 'function') {
          return target[name].bind(target);
        }
      }
    }
    return null;
  }, [entityName]);

  const getDeleteFn = useCallback(() => {
    if (typeof deleteItemRef.current === 'function') return deleteItemRef.current;
    const client = apiClientRef.current;
    if (!client) return null;
    const targets = [client, client.default, client.client].filter(Boolean);
    const candidateNames = ['remove', 'delete', `delete${entityName}`, `remove${entityName}`];
    for (const target of targets) {
      for (const name of candidateNames) {
        if (typeof target[name] === 'function') {
          return target[name].bind(target);
        }
      }
    }
    return null;
  }, [entityName]);

  // Stable serialized key for extraParams to guarantee deep value equality
  const serializedExtra = useMemo(() => JSON.stringify(extraParams || {}), [extraParams]);

  const queryParams = useMemo(() => {
    let parsedExtra = {};
    try {
      parsedExtra = JSON.parse(serializedExtra);
    } catch {}
    return {
      page,
      pageSize,
      viewMode,
      searchText: searchText.trim(),
      ...parsedExtra,
    };
  }, [page, pageSize, viewMode, searchText, serializedExtra]);

  const loadItems = useCallback(async () => {
    const listFn = getListFn();
    if (!listFn) {
      const err = `[useMasterList] Could not resolve list method for ${entityName}.`;
      console.error(err, { apiClient: apiClientRef.current, fetchList: fetchListRef.current });
      setError(err);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const response = await listFn(queryParams);
      setItems(response?.items || []);
      setMeta({
        totalCount: response?.total_count ?? 0,
        page: response?.page ?? 1,
        pageSize: response?.page_size ?? pageSize,
        totalPages: response?.total_pages ?? 1,
      });
    } catch (requestError) {
      console.error(`[useMasterList] Error loading ${entityName}:`, requestError);
      const msg = requestError?.response?.data?.detail || requestError?.message || `Failed to load ${entityName.toLowerCase()}s.`;
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsLoading(false);
    }
  }, [getListFn, queryParams, pageSize, entityName]);

  // CRITICAL: Triggers only when primitive search/filter state actually changes
  useEffect(() => {
    loadItems();
  }, [page, pageSize, viewMode, searchText, serializedExtra, loadItems]);

  const handleArchive = async (id) => {
    const archiveFn = getArchiveFn();
    if (!archiveFn) return;
    try {
      const res = await archiveFn(id);
      appToast.success(res?.message || `${entityName} status updated successfully.`);
      loadItems();
    } catch (e) {
      console.error(`[useMasterList] Error archiving ${entityName}:`, e);
      const msg = e?.response?.data?.detail || e?.message || `Failed to update ${entityName.toLowerCase()} status.`;
      appToast.error(typeof msg === 'string' ? msg : `Failed to update ${entityName.toLowerCase()} status.`);
    }
  };

  const handleDelete = async (id) => {
    const deleteFn = getDeleteFn();
    if (!deleteFn) return;
    const confirmed = await appConfirm({
      title: `Delete ${entityName}`,
      message: `Are you sure you want to delete this ${entityName.toLowerCase()}? This action will archive and remove it from active use.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await deleteFn(id);
        appToast.success(`${entityName} deleted successfully.`);
        loadItems();
      } catch (e) {
        console.error(`[useMasterList] Error deleting ${entityName}:`, e);
        const msg = e?.response?.data?.detail || e?.message || `Failed to delete ${entityName.toLowerCase()}.`;
        appToast.error(typeof msg === 'string' ? msg : `Failed to delete ${entityName.toLowerCase()}.`);
      }
    }
  };

  const resetFilters = () => {
    setSearchText('');
    setPage(1);
  };

  const actions = {
    setViewMode: (mode) => {
      setViewMode(mode);
      setPage(1);
    },
    setSearchText: (text) => {
      setSearchText(text);
      setPage(1);
    },
    setPage,
    setPageSize,
    resetFilters,
    handleArchive,
    handleDelete,
    refresh: loadItems,
  };

  return {
    state: {
      viewMode,
      searchText,
      page,
      pageSize,
      isLoading,
      error,
      rows: items,
      totalCount: meta.totalCount,
      totalPages: meta.totalPages,
    },
    actions,
  };
}
